import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";
import createAdHandler from "monetag-tg-sdk";
import "../index.css";

const VISIT_WAIT_MS = 30000;
const CLAIM_DELAY_MS = 1 * 60 * 1000;
const PREVIEW_LIMIT = 3;

const BOUNTY_TASK_ENABLED = true;

const isBoinkersTask = (task) => {
  const title = String(task?.title || "").trim().toLowerCase();

  return title.startsWith("spin the slot to claim");
};

const ADSGRAM_TASK_BLOCK_ID = "task-21112";
// const ADSGRAM_TASK_BLOCK_ID = "task-21088";
const ADSGRAM_DEBUG = false;
const ADSGRAM_REWARD_AMOUNT = 25;
const ADSGRAM_REWARD_CURRENCY = "sparks";

const MONETAG_TASK_ZONE_ID = 11758650;
const MONETAG_TASK_REWARD_AMOUNT = 25;
const MONETAG_TASK_REWARD_CURRENCY = "sparks";

const monetagTaskAd =
  createAdHandler(MONETAG_TASK_ZONE_ID);

const API_BASE = process.env.REACT_APP_API_URL;

const normalizePrizeType = (type) =>
  String(type || "").trim().toUpperCase().replace(/[\s-]+/g, "_");

const normalizeFrequency = (task) => {
  const rawFrequency = String(task?.frequency || "").toLowerCase().trim();
  const rawCategory = String(task?.category || "").toLowerCase().trim();

  if (
    rawFrequency === "partnership" ||
    rawFrequency === "partnerships" ||
    rawCategory === "partnership" ||
    rawCategory === "partnerships"
  ) {
    return "partnerships";
  }

  if (rawFrequency === "daily") return "daily";
  if (rawFrequency === "weekly") return "weekly";
  if (rawFrequency === "monthly") return "monthly";

  return "once";
};

const getBalanceRewardFromTask = (task) => {
  const prizeType = normalizePrizeType(
    task?.prizeItemType || task?.prize_item_type
  );

  if (prizeType.includes("RUNE")) return "runes";
  if (prizeType.includes("SPARK")) return "sparks";

  const rewardCurrency = String(
    task?.rewardCurrency || task?.reward_currency || "sparks"
  ).toLowerCase();

  if (!task?.prizeItemId && !task?.prize_item_id) {
    return rewardCurrency === "runes" ? "runes" : "sparks";
  }

  return null;
};

const getPrizeIcon = (task) => {
  const prizeType = normalizePrizeType(
    task?.prizeItemType || task?.prize_item_type
  );

  const rewardCurrency = String(
    task?.rewardCurrency || task?.reward_currency || "sparks"
  ).toLowerCase();

  if (prizeType.includes("RUNE")) return "/runes.png";
  if (prizeType.includes("SPARK")) return "/sparks.png";
  if (prizeType.includes("CARD_PIECE")) return "/pieces.png";
  if (prizeType.includes("BOOST")) return "/energy.png";
  if (prizeType.includes("ABILITY")) return "/fightlog.png";
  if (prizeType.includes("SEASONAL_ACCESSORY")) return "/fragment.png";
  if (prizeType.includes("SPINS")) return "/fortuneicon.png";
  if (prizeType.includes("CARD")) return "/pack.png";
  if (prizeType.includes("CHEST") || prizeType.includes("PACK")) return "/pack.png";

  if (rewardCurrency === "runes") return "/runes.png";
  if (!task?.prizeItemId && !task?.prize_item_id) return "/sparks.png";
  return "/gift.png";
};

const getTaskDateValue = (task) => {
  const raw =
    task?.createdAt ||
    task?.created_at ||
    task?.updatedAt ||
    task?.updated_at ||
    task?.publishedAt ||
    task?.published_at ||
    task?.id;

  const asDate = new Date(raw).getTime();
  if (Number.isFinite(asDate)) return asDate;

  const asNumber = Number(raw);
  return Number.isFinite(asNumber) ? asNumber : 0;
};

const newestFirst = (list) => {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    const dateDiff = getTaskDateValue(b) - getTaskDateValue(a);
    if (dateDiff !== 0) return dateDiff;
    return Number(b?.id || 0) - Number(a?.id || 0);
  });
};

const getTaskAvatarSrc = (task) => {
  return task?.avatar_url || task?.avatarUrl || "./calendar.png";
};

const getUnfinishedCount = (list) => {
  return (list || []).filter((task) => !task.claimedAt).length;
};

const formatVisitCountdown = (ms) => {
  const secs = Math.max(0, Math.ceil(Number(ms || 0) / 1000));
  return `${secs}s`;
};

export const AdsgramTaskCard = React.memo(function AdsgramTaskCard({
  blockId,
  debug,
  rewardAmount,
  rewardCurrency,
  onReward,
  onReady,
  onNotFound,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const markReady = () => onReady?.();

    const handleRewardEvent = (event) => {
      const detail = event?.detail;
      const earnedBlockId =
        typeof detail === "string" && /^task-\d+$/.test(detail) ? detail : null;
      onReward?.({ blockId: earnedBlockId || blockId });
    };

    const handleErrorEvent = (e) => {
      console.warn("AdsGram onError:", e?.detail);
      markReady();
    };

    const handleNotFoundEvent = (e) => {
      console.warn("AdsGram onBannerNotFound:", e?.detail);
      onNotFound?.();
      markReady();
    };

    el.addEventListener("reward", handleRewardEvent);
    el.addEventListener("onError", handleErrorEvent);
    el.addEventListener("onBannerNotFound", handleNotFoundEvent);
    el.addEventListener("onTooLongSession", handleErrorEvent);

    const fallbackTimer = setTimeout(markReady, 1500);

    return () => {
      clearTimeout(fallbackTimer);
      el.removeEventListener("reward", handleRewardEvent);
      el.removeEventListener("onError", handleErrorEvent);
      el.removeEventListener("onBannerNotFound", handleNotFoundEvent);
      el.removeEventListener("onTooLongSession", handleErrorEvent);
    };
  }, [blockId, onReward, onReady, onNotFound]);

  if (!blockId) return null;

  return (
    <adsgram-task
      ref={ref}
      data-block-id={blockId}
      data-debug={debug ? "true" : "false"}
      data-debug-console={debug ? "true" : "false"}
      className="task-card"
      style={{
        paddingRight: "0px",
        height: "64px",
        color: "#fff",
        textAlign: "left",
        marginBottom: "0px",
        fontWeight: "bold",
        fontSize: "14px",
      }}
    >
      <div
        slot="reward"
        className="task-reward"
        style={{
          backgroundImage: `url('${
            rewardCurrency === "runes" ? "/runes.png" : "/sparks.png"
          }')`,
          backgroundSize: "18px 18px",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left center",
          fontSize: "16px",
          color: "#f3e14d",
          paddingLeft: "21px",
          marginTop: "4px",
          textAlign: "left",
          fontWeight: "normal",
          fontFamily: '"Fredoka", sans-serif',
          fontWeight: 600
        }}
      >
        {rewardAmount}
        <span style={{ paddingLeft: 4 }}>
          {rewardCurrency === "runes" ? " Runes" : " Sparks"}
        </span>
      </div>

      <div slot="button" style={{marginTop: 0, marginRight: 10}} className="game-button-small yellow-button">
        OPEN
      </div>
      <div slot="claim" style={{marginTop: 0, marginRight: 10}} className="game-button-small green-button">
        CLAIM
      </div>
      <div
        slot="done"
        className="visittask"
        style={{
          paddingRight: "0px",
          textAlign: "right",
          backgroundColor: "transparent",
          border: "0px",
        }}
      >
        <img
          src="./v2/check.png"
          alt=""
          style={{ marginRight: "-10px", float: "right" }}
        />
      </div>
    </adsgram-task>
  );
});

function TasksSection({
  title,
  count,
  showCount = true,
  actionLabel = "SHOW ALL",
  showAction = true,
  onClick,
  children,
  titleClassName = "",
}) {
  const centeredTitle =
    titleClassName.includes("modal_title_special") ||
    titleClassName.includes("modal_title_partnership");

  return (
    <section style={{ marginLeft: -10, marginRight: -10, marginTop: 0, marginBottom: 0 }}>
      <div
        className={`modal_title tasks_modal_title ${titleClassName}`}
        style={{
          textAlign: centeredTitle ? "center" : "left",
          paddingLeft: centeredTitle ? 0 : 28,
          boxSizing: "border-box",
          marginTop: 0,
          marginBottom: -4,
        }}
      >
        {title}
        {showCount ? ` (${count})` : ""}

        {showAction && onClick && (
          <button type="button" onClick={onClick} className="modal_bt">
            {actionLabel}
          </button>
        )}
      </div>

      <div className="modal_body modal_body_2" style={{ marginTop: 0, paddingTop: 14 }}>
        {children}
      </div>
    </section>
  );
}

function EmptyTasks({ text }) {
  return (
    <div
      className="shadow1"
      style={{
        color: "#fff",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontSize: 16,
        padding: "10px 14px",
        boxSizing: "border-box",
      }}
    >
      {text}
    </div>
  );
}

const TasksPage = ({ user, setUser, onNavigate }) => {
  const [tasks, setTasks] = useState([]);
    const [storyTasks, setStoryTasks] = useState([]);
  const [storyClaimingId, setStoryClaimingId] = useState(null);
  const [storyNow, setStoryNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [visitTimers, setVisitTimers] = useState({});
  const [countdowns, setCountdowns] = useState({});
  const [successModalTask, setSuccessModalTask] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeModal, setActiveModal] = useState(null);

  const [monetagTaskState, setMonetagTaskState] = useState({
  claimedToday: false,
  canWatch: true,
  rewardAmount: MONETAG_TASK_REWARD_AMOUNT,
  rewardCurrency: MONETAG_TASK_REWARD_CURRENCY,
});

const [monetagTaskLoading, setMonetagTaskLoading] = useState(false);
const [monetagTaskMessage, setMonetagTaskMessage] = useState("");

const [bountyReferralLink, setBountyReferralLink] = useState("");
const [bountyClaimedAt, setBountyClaimedAt] = useState(null);

  const sparksRef = useRef(0);
  const runesRef = useRef(0);

  useEffect(() => {
    sparksRef.current = Number(user?.sparks ?? 0);
    runesRef.current = Number(user?.runes ?? 0);
  }, [user?.sparks, user?.runes]);

  const YIR_TASK = useMemo(
    () => ({
      id: "220264",
      title: "Share The Season 3",
      url: "yearreview",
      reward: 10,
      rewardCurrency: "runes",
      frequency: "once",
      verificationType: "manual",
      visitedAt: null,
      claimedAt: null,
      avatarUrl: "/calendar.png",
      createdAt: "220264",
    }),
    []
  );

  const BOUNTY_TASK = useMemo(
  () => ({
    id: "220265",
    title: "Share The Bounty",
    url: "bountystory",
    reward: 2500,
    rewardCurrency: "sparks",
    frequency: "once",
    verificationType: "manual",
    visitedAt: null,
    claimedAt: bountyClaimedAt,
    avatarUrl: "/calendar.png",
    createdAt: "220265",
    isBountyStory: true,
  }),
  [bountyClaimedAt]
);

  const grouped = useMemo(() => {
    const next = {
      daily: [],
      weekly: [],
      monthly: [],
      once: [],
      partnerships: [],
    };

        (tasks || []).forEach((task) => {
      if (isBoinkersTask(task)) return;

      next[normalizeFrequency(task)].push(task);
    });

    return {
      daily: newestFirst(next.daily),
      weekly: newestFirst(next.weekly),
      monthly: newestFirst(next.monthly),
      once: newestFirst(next.once),
      partnerships: newestFirst(next.partnerships),
    };
  }, [tasks]);

  const sections = useMemo(
    () => [
      { key: "daily", label: "Daily Tasks" },
      { key: "weekly", label: "Weekly Tasks" },
      { key: "monthly", label: "Monthly Tasks" },
      { key: "once", label: "One-Time Tasks" },
    ],
    []
  );

  const refreshBalances = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/me`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
      },
    });

    if (!res.ok) return null;

    const data = await res.json().catch(() => ({}));

    const sparks = Number(data?.user?.sparks ?? data?.sparks ?? NaN);
    const runes = Number(data?.user?.runes ?? data?.runes ?? NaN);

    setUser?.((currentUser) => ({
      ...(currentUser || {}),
      sparks: Number.isFinite(sparks) ? sparks : currentUser?.sparks ?? 0,
      runes: Number.isFinite(runes) ? runes : currentUser?.runes ?? 0,
    }));

    return { sparks, runes };
  }, [setUser]);

  const onAdsgramReward = useCallback(async () => {
    setUser?.((currentUser) => ({
      ...(currentUser || {}),
      sparks:
        ADSGRAM_REWARD_CURRENCY === "sparks"
          ? Number(currentUser?.sparks || 0) + ADSGRAM_REWARD_AMOUNT
          : currentUser?.sparks,
      runes:
        ADSGRAM_REWARD_CURRENCY === "runes"
          ? Number(currentUser?.runes || 0) + ADSGRAM_REWARD_AMOUNT
          : currentUser?.runes,
    }));

    const beforeSparks = sparksRef.current;
    const beforeRunes = runesRef.current;

    for (let i = 0; i < 12; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 500));

      const updated = await refreshBalances();
      if (!updated) continue;

      if (
        (Number.isFinite(updated.sparks) && updated.sparks > beforeSparks) ||
        (Number.isFinite(updated.runes) && updated.runes > beforeRunes)
      ) {
        console.log("AdsGram backend confirmed reward ✅");
        return;
      }
    }

    console.warn("AdsGram backend did not update in time ⚠️");
  }, [refreshBalances, setUser]);

  useEffect(() => {
  if (!user?.id) return;

  let cancelled = false;

  const loadBountyReferralLink = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/friends`,
        {
          headers: {
            Authorization:
              `Bearer ${localStorage.getItem("token")}`,

            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data =
        await res.json().catch(() => ({}));

      if (!res.ok || cancelled) return;

      const referralCode =
        String(data?.referralCode || "").trim();

      if (!referralCode) return;

      const botUsername =
        process.env.REACT_APP_TELEGRAM_BOT_USERNAME ||
        "taptopiaio_bot";

      setBountyReferralLink(
        `https://t.me/${botUsername}?startapp=${encodeURIComponent(
          referralCode
        )}`
      );

    } catch (err) {
      console.error(
        "[Bounty] Failed to load referral link",
        err
      );
    }
  };

  loadBountyReferralLink();

  return () => {
    cancelled = true;
  };
}, [user?.id]);

  const fetchMonetagTaskState = useCallback(async () => {
  try {
    const res = await fetch(
      `${API_BASE}/api/tasks/monetag/state`,
      {
        headers: {
          Authorization:
            `Bearer ${localStorage.getItem("token")}`,

          "X-TG-INITDATA":
            window.Telegram?.WebApp?.initData || "",
        },
      }
    );

    const data =
      await res.json().catch(() => ({}));

    if (!res.ok) return null;

    setMonetagTaskState({
      claimedToday:
        Boolean(data.claimedToday),

      canWatch:
        Boolean(data.canWatch),

      rewardAmount:
        Number(
          data.rewardAmount ||
          MONETAG_TASK_REWARD_AMOUNT
        ),

      rewardCurrency:
        String(
          data.rewardCurrency ||
          MONETAG_TASK_REWARD_CURRENCY
        ).toLowerCase(),
    });

    return data;

  } catch (err) {
    console.error(
      "Failed to load Monetag task state",
      err
    );

    return null;
  }
}, []);

const fetchStoryTasks = useCallback(async () => {
  try {
    const res = await fetch(`${API_BASE}/api/tasks/stories`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
      },
    });

    if (!res.ok) {
      throw new Error("Failed to load Story Tasks");
    }

       const data = await res.json();

    setStoryTasks(
      Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : []
    );
  } catch (err) {
    console.error("Story Tasks:", err);
  }
}, []);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/tasks`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
      });

      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.tasks)
        ? data.tasks
        : [];

        const bountyApiTask = list.find(
  (task) =>
    Number(task.id) === 220265 ||
    String(task.url || "") === "bountystory"
);

setBountyClaimedAt(
  bountyApiTask?.claimedAt ||
  bountyApiTask?.claimed_at ||
  null
);

      const filtered = list.filter(
  (task) =>
    Number(task.id) !== 2025 &&
    String(task.url || "") !== "year-in-review" &&
    Number(task.id) !== 220265 &&
    String(task.url || "") !== "bountystory"
);

      setTasks([YIR_TASK, ...filtered]);

      const seeded = {};
      [YIR_TASK, ...filtered].forEach((task) => {
        if (task.visitedAt && !task.claimedAt) {
          const ms = Math.max(
            0,
            CLAIM_DELAY_MS -
              (Date.now() - new Date(task.visitedAt).getTime())
          );

          if (ms > 0) seeded[task.id] = ms;
        }
      });
      setCountdowns(seeded);
    } catch (err) {
      console.error("Failed to load tasks", err);
      setErrorMessage("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [YIR_TASK]);

useEffect(() => {
  fetchTasks();
  fetchMonetagTaskState();
  fetchStoryTasks();
}, [
  fetchTasks,
  fetchMonetagTaskState,
  fetchStoryTasks,
]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdowns((prev) => {
        const keys = Object.keys(prev);
        if (keys.length === 0) return prev;

        const updated = {};
        keys.forEach((taskId) => {
          const left = Number(prev[taskId] || 0) - 1000;
          if (left > 0) updated[taskId] = left;
        });

        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    const timer = setInterval(() => {
      setStoryNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getStoryWaitSeconds = (story) => {
    if (!story?.visited_at || story?.claimed_at) return 0;

    const visitedTime = new Date(story.visited_at).getTime();

    if (!Number.isFinite(visitedTime)) return 30;

    const elapsed = storyNow - visitedTime;

    return Math.max(0, Math.ceil((30000 - elapsed) / 1000));
  };
  
  const showMonetagTaskMessage = useCallback(
  (message, ms = 1800) => {
    setMonetagTaskMessage(message);

    setTimeout(() => {
      setMonetagTaskMessage("");
    }, ms);
  },
  []
);

const handleMonetagTask = useCallback(async () => {
  if (monetagTaskLoading) return;

  if (!monetagTaskState.canWatch) {
    showMonetagTaskMessage(
      "COMPLETED TODAY",
      1800
    );

    return;
  }

  setMonetagTaskLoading(true);
  setMonetagTaskMessage("");

  try {
    const token =
      localStorage.getItem("token");

    // ------------------------------------------
    // Prepare
    // ------------------------------------------

    const prepareRes = await fetch(
      `${API_BASE}/api/tasks/monetag/prepare`,
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",

          Authorization:
            `Bearer ${token}`,

          "X-TG-INITDATA":
            window.Telegram?.WebApp?.initData || "",
        },

        body: JSON.stringify({}),
      }
    );

    const prepareData =
      await prepareRes.json().catch(() => ({}));

    if (!prepareRes.ok) {
      if (
        prepareData?.error ===
        "MONETAG_TASK_DAILY_LIMIT"
      ) {
        await fetchMonetagTaskState();

        showMonetagTaskMessage(
          "COMPLETED TODAY",
          1800
        );

        return;
      }

      throw new Error(
        prepareData?.error ||
        "Could not prepare Monetag task"
      );
    }

    const ymid = String(
      prepareData?.ymid || ""
    ).trim();

    if (!ymid) {
      throw new Error(
        "Backend did not return ymid"
      );
    }

    // ------------------------------------------
    // Rewarded Popup
    // ------------------------------------------

    await monetagTaskAd({
      type: "pop",

      ymid,

      requestVar:
        "special_task_monetag",
    });

    // IMPORTANT:
    // .then() does NOT mean reward.
    // Wait for backend postback.
    // ------------------------------------------

    let rewarded = false;

    for (
      let attempt = 0;
      attempt < 12;
      attempt++
    ) {
      const statusRes = await fetch(
        `${API_BASE}/api/tasks/monetag/status/${encodeURIComponent(
          ymid
        )}`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,

            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const statusData =
  await statusRes.json().catch(() => ({}));

if (
  statusRes.ok &&
  statusData?.status === "non_valued"
) {
  showMonetagTaskMessage(
    "NO REWARD AVAILABLE — TRY AGAIN",
    2000
  );

  return;
}

if (
  statusRes.ok &&
  statusData?.rewarded === true
) {
  rewarded = true;
  break;
}

      await new Promise((resolve) =>
        setTimeout(resolve, 1000)
      );
    }

    if (rewarded) {
      await refreshBalances();
      await fetchMonetagTaskState();

      showMonetagTaskMessage(
        `+${MONETAG_TASK_REWARD_AMOUNT} SPARKS ADDED!`,
        1800
      );

    } else {
      showMonetagTaskMessage(
        "REWARD IS PROCESSING...",
        2000
      );

      fetchMonetagTaskState().catch(() => {});
    }

  } catch (err) {
    console.error(
      "[Monetag Special Task]",
      err
    );

    showMonetagTaskMessage(
      err?.message || "POPUP FAILED",
      2000
    );

  } finally {
    setMonetagTaskLoading(false);
  }

}, [
  monetagTaskLoading,
  monetagTaskState.canWatch,
  refreshBalances,
  fetchMonetagTaskState,
  showMonetagTaskMessage,
]);

  const isClaimable = (task) => {
    if (!task.visitedAt || task.claimedAt) return false;
    const visitedTime = new Date(task.visitedAt).getTime();
    return Date.now() - visitedTime > CLAIM_DELAY_MS;
  };


  const handleClaim = async (taskId) => {
    const task = tasks.find((item) => String(item.id) === String(taskId));
    if (!task) return;

    const prevTasks = tasks;
    const prevUser = user;
    const nowIso = new Date().toISOString();

    setTasks((prev) =>
      prev.map((item) =>
        String(item.id) === String(taskId)
          ? { ...item, claimedAt: nowIso }
          : item
      )
    );

    if (typeof setUser === "function") {
      const balanceRewardType = getBalanceRewardFromTask(task);
      const reward = Number(task.reward || 0);

      setUser((currentUser) => {
        const next = { ...(currentUser || {}) };

        if (balanceRewardType === "runes") {
          next.runes = Number(next.runes || 0) + reward;
        } else if (balanceRewardType === "sparks") {
          next.sparks = Number(next.sparks || 0) + reward;
        }

        return next;
      });
    }

    try {
      const res = await fetch(`${API_BASE}/api/tasks/claim/${taskId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const code = String(errData?.error || "");

        setTasks(prevTasks);
        if (typeof setUser === "function") setUser(prevUser);

        if (code === "HUMAN_CHECK_REQUIRED") {
          onNavigate?.("humancheck", {
            returnTo: "tasks",
            returnParams: {},
          });
          return;
        }

        setErrorMessage(errData.error || "Claim failed");
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (data?.user && typeof setUser === "function") {
        setUser((currentUser) => ({
          ...(currentUser || {}),
          sparks:
            data.user.sparks !== undefined
              ? Number(data.user.sparks)
              : currentUser?.sparks ?? 0,
          runes:
            data.user.runes !== undefined
              ? Number(data.user.runes)
              : currentUser?.runes ?? 0,
        }));
      }

      if (data?.claimedAt) {
        setTasks((prev) =>
          prev.map((item) =>
            String(item.id) === String(taskId)
              ? { ...item, claimedAt: data.claimedAt }
              : item
          )
        );
      }
    } catch (err) {
      console.error("Claim failed", err);
      setTasks(prevTasks);
      if (typeof setUser === "function") setUser(prevUser);
      setErrorMessage("Claim failed");
    }
  };

    const handleVerify = async (task) => {
    try {
      const res = await fetch(`${API_BASE}/api/tasks/verify/${task.id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrorMessage(data.error || "Verification failed");
        return;
      }

      const nowIso = new Date().toISOString();
      setTasks((prev) =>
        prev.map((item) =>
          String(item.id) === String(task.id)
            ? { ...item, visitedAt: nowIso }
            : item
        )
      );

      await handleClaim(task.id);
    } catch (err) {
      setErrorMessage("Verification failed");
    }
  };

  const handleStoryShare = async (story) => {
    const tg = window.Telegram?.WebApp;

    if (!tg || typeof tg.shareToStory !== "function") {
      setErrorMessage("Telegram Stories are not supported.");
      return;
    }

    if (!story.image_url) {
      setErrorMessage("This Story has no image configured.");
      return;
    }

    if (story.claimed_at) return;

    try {
      const params = {
        text: story.description || "",
      };

      if (story.widget_url) {
        params.widget_link = {
          url: story.widget_url,
          name: story.widget_label || "PLAY TAPTOPIA",
        };
      }

      // Keep Telegram sharing inside the user's click.
      tg.shareToStory(story.image_url, params);

      const res = await fetch(
        `${API_BASE}/api/tasks/stories/${story.id}/visit`,
        {
          method: "POST",
          headers: {
            Authorization:
              `Bearer ${localStorage.getItem("token")}`,
            "X-TG-INITDATA":
              tg.initData || "",
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.error || "Could not register Story visit."
        );
      }

      setStoryTasks((prev) =>
        prev.map((item) =>
          String(item.id) === String(story.id)
            ? {
                ...item,
                visited_at: data.visitedAt,
                claimed_at: data.claimedAt || null,
              }
            : item
        )
      );

    } catch (err) {
      console.error("[Story Task Share]", err);
      setErrorMessage(
        err.message || "Could not share Story."
      );
    }
  };



    const handleStoryClaim = async (story) => {
    if (storyClaimingId !== null || story.claimed_at || story.claimed) return;

    if (getStoryWaitSeconds(story) > 0) return;

    setStoryClaimingId(story.id);

    try {
      const res = await fetch(
        `${API_BASE}/api/tasks/stories/${story.id}/claim`,
        {
          method: "POST",
          headers: {
            Authorization:
              `Bearer ${localStorage.getItem("token")}`,
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 409) {
          await fetchStoryTasks();
          return;
        }

        if (data.error === "HUMAN_CHECK_REQUIRED") {
          onNavigate?.("humancheck", {
            returnTo: "tasks",
            returnParams: {},
          });
          return;
        }

        throw new Error(
          data.error || "Could not claim Story reward."
        );
      }

      setStoryTasks((prev) =>
        prev.map((item) =>
          String(item.id) === String(story.id)
            ? {
                ...item,
                claimed_at: data.claimedAt,
                claimed: true,
              }
            : item
        )
      );

      if (data.user) {
        setUser?.((prev) => ({
          ...(prev || {}),
          sparks: data.user.sparks !== undefined
            ? Number(data.user.sparks)
            : prev?.sparks ?? 0,
          runes: data.user.runes !== undefined
            ? Number(data.user.runes)
            : prev?.runes ?? 0,
        }));
      }

      setSuccessModalTask(null);

      await refreshBalances();

    } catch (err) {
      console.error("[Story Task Claim]", err);
      setErrorMessage(err.message || "Story claim failed.");
    } finally {
      setStoryClaimingId(null);
    }
  };



  const handleBountyStoryShare = async () => {
  const tg = window?.Telegram?.WebApp;

  if (
    !tg ||
    typeof tg.shareToStory !== "function"
  ) {
    setErrorMessage(
      "Telegram Stories are not supported in this client."
    );

    return;
  }

  const mediaUrl =
    "https://taptopia.io/blog/bounty.png";

  const text =
    "Think you can collect my bounty? Bring your best deck to the arena! @taptopiaio_bot";

  try {
    const storyParams = {
      text,
    };

    /*
     * Add the user's normal referral link.
     *
     * Premium users can publish the clickable
     * Story widget. Others still share the Story.
     */
    if (bountyReferralLink) {
      storyParams.widget_link = {
        url: bountyReferralLink,
        name: "JOIN TAPTOPIA",
      };
    }

    /*
     * Open Telegram Story editor.
     */
    tg.shareToStory(
      mediaUrl,
      storyParams
    );

    /*
     * Claim the share reward.
     */
    const res = await fetch(
      `${API_BASE}/api/me/bounty/claim`,
      {
        method: "POST",

        headers: {
          "Content-Type": "application/json",

          Authorization:
            `Bearer ${localStorage.getItem("token")}`,

          "X-TG-INITDATA":
            tg.initData || "",
        },

        body: JSON.stringify({}),
      }
    );

    const data =
      await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data?.error ||
        "Could not claim bounty reward."
      );
    }

    /*
     * Mark the task as completed even if
     * the user had already claimed it.
     */
    setBountyClaimedAt(
      new Date().toISOString()
    );

    /*
     * Already claimed previously.
     */
    if (data?.alreadyClaimed) {
      return;
    }

    /*
     * Update visible balances.
     */
    setUser?.((currentUser) => ({
      ...(currentUser || {}),

      sparks:
        data?.sparks !== undefined
          ? Number(data.sparks)
          : currentUser?.sparks ?? 0,

      runes:
        data?.runes !== undefined
          ? Number(data.runes)
          : currentUser?.runes ?? 0,
    }));

    alert(
      "BOUNTY REWARD CLAIMED!\n\n" +
      "+2,500 Sparks\n" +
      "+25 Runes"
    );

  } catch (err) {
    console.error(
      "[Bounty Story]",
      err
    );

    setErrorMessage(
      err?.message ||
      "Could not share bounty Story."
    );
  }
};

  const handleVisit = async (task) => {
  if (task?.url === "bountystory") {
    await handleBountyStoryShare();
    return;
  }

  if (task?.url === "yearreview") {
    onNavigate?.("yearreview");
    return;
  }
    window.open(task.url, "_blank");

    const targetTime = Date.now() + VISIT_WAIT_MS;
    setVisitTimers((prev) => ({ ...prev, [task.id]: targetTime }));
    setCountdowns((prev) => ({ ...prev, [task.id]: VISIT_WAIT_MS }));

    try {
      await fetch(`${API_BASE}/api/tasks/visit/${task.id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
      });
    } catch (err) {
      console.error("Visit failed", err);
    }

    setTimeout(() => {
      setSuccessModalTask(task);
    }, VISIT_WAIT_MS);
  };

  const renderTaskCard = (task, idx) => {
    const countdown = countdowns[task.id];
    const currency = String(
      task.rewardCurrency || task.reward_currency || "sparks"
    ).toLowerCase();
    const isRunes = currency === "runes";
    const isItemReward = !!(task.prizeItemId || task.prize_item_id);
    const itemRewardName = task.prizeItemName || task.prize_item_name || "Item";
    const rewardIcon = getPrizeIcon(task);
    const claimed = !!task.claimedAt;

    return (
      <motion.div
        key={task.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.25, delay: Math.min(idx, 5) * 0.04 }}
        className="task-card"
        style={{
          
          opacity: claimed ? 0.72 : 1,
        }}
      >
        <div className="icon">
          <img
            src={getTaskAvatarSrc(task)}
            style={{
              height: "32px",
              width: "32px",
              objectFit: "cover",
              borderRadius: "6px",
            }}
            onError={(e) => {
              e.currentTarget.src = "./calendar.png";
            }}
            alt=""
          />
        </div>

        <div className="task" style={{ minWidth: 0 }}>
          <h3
  style={{
    fontSize: isBoinkersTask(task) ? "12px" : "14px",
    lineHeight: isBoinkersTask(task) ? "14px" : undefined,
    marginBottom: isBoinkersTask(task) ? "2px" : "4px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: isBoinkersTask(task) ? "normal" : "nowrap",

    ...(isBoinkersTask(task)
      ? {
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          maxHeight: "28px",
        }
      : {}),
  }}
>
  {task.title}
</h3>

          <div
            className="task-reward"
            style={{
              backgroundImage: `url('${rewardIcon}')`,
              backgroundSize: "18px 18px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
              fontSize: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {task?.url === "bountystory" ? (
  <span style={{ paddingLeft: 0 }}>
    2500 Sparks 25 Runes
  </span>
) : isItemReward ? (
  <span style={{ paddingLeft: 4 }}>
    {itemRewardName}
  </span>
) : (
  <>
    {task.reward}
    <span style={{ paddingLeft: 4 }}>
      {isRunes ? " Runes" : " Sparks"}
    </span>
  </>
)}

            <span style={{fontSize: 10, paddingLeft: "4px", color: "#ff9800" }}>
              {countdown
                ? `Checking... (${formatVisitCountdown(countdown)})`
                : ""}
            </span>
          </div>
        </div>

        {task.verificationType === "telegram_name_contains" ? (
          !claimed ? (
            <div className="action">
              <button
                type="button"
                className="game-button-small green-button"
                onClick={() => handleVerify(task)}
              >
                VERIFY
              </button>
            </div>
          ) : (
            <div className="action">
              <img src="./v2/check.png" alt="" style={{ float: "right", marginTop: "15px" }} />
            </div>
          )
        ) : !task.visitedAt ? (
          <div className="action">
            <button
  type="button"
  className="game-button-small yellow-button"
  onClick={() => handleVisit(task)}
  disabled={!!visitTimers[task.id] || !!task.visitedAt || claimed}
  style={{
    opacity: !!visitTimers[task.id] || !!task.visitedAt || claimed ? 0.45 : 1,
  }}
>
  {task?.url === "bountystory" ? "SHARE" : "OPEN"}
</button>
          </div>
        ) : !claimed ? (
          <div className="action">
            <button
              type="button"
              className="game-button-small green-button"
              onClick={() => handleClaim(task.id)}
              disabled={!isClaimable(task)}
            >
              {isClaimable(task) ? "CLAIM" : " WAIT"}
            </button>
          </div>
        ) : (
          <div className="action">
            <img src="./v2/check.png" alt="" style={{ float: "right", marginTop: "15px" }} />
          </div>
        )}
      </motion.div>
    );
  };

const renderStoryTask = (story) => (
  <div
  key={`story-${story.id}`}
  className="task-card"
  style={{ height: 64 }}
>
    <div className="icon">
      <img
        src={story.image_url || "/calendar.png"}
        alt=""
        style={{
          width: 32,
          height: 32,
          objectFit: "cover",
          borderRadius: 6,
        }}
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = "/calendar.png";
        }}
      />
    </div>

    <div className="task" style={{ minWidth: 0 }}>
      <h3
        style={{
          fontSize: 14,
          marginBottom: 4,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {story.title}
      </h3>

      <div
  className="task-reward"
  style={{
    backgroundImage: `url('${
      story.reward_currency === "runes"
        ? "/runes.png"
        : "/sparks.png"
    }')`,
    backgroundSize: "18px 18px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left center",
    fontSize: "16px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }}
>
  {story.reward}
  <span style={{ paddingLeft: 4 }}>
    {story.reward_currency === "runes"
      ? " Runes"
      : " Sparks"}
  </span>
</div>
    </div>

        <div className="action">
      {story.claimed_at || story.claimed ? (
        <img
          src="./v2/check.png"
          alt="Claimed"
          style={{
            float: "right",
            marginTop: 15,
          }}
        />
      ) : !story.visited_at ? (
        <button
          type="button"
          className="game-button-small yellow-button"
          onClick={() => handleStoryShare(story)}
          disabled={!story.image_url}
        >
          SHARE
        </button>
      ) : (
        <button
          type="button"
          className="game-button-small green-button"
          onClick={() => handleStoryClaim(story)}
          disabled={
            getStoryWaitSeconds(story) > 0 ||
            storyClaimingId !== null
          }
        >
          {storyClaimingId === story.id
            ? "CLAIMING..."
            : getStoryWaitSeconds(story) > 0
            ? `WAIT ${getStoryWaitSeconds(story)}s`
            : "CLAIM"}
        </button>
      )}
    </div>
  </div>
);

  const boinkersTask = useMemo(
    () => tasks.find(isBoinkersTask) || null,
    [tasks]
  );

  const adsgramNode = useMemo(
    () => (
      <AdsgramTaskCard
        blockId={ADSGRAM_TASK_BLOCK_ID}
        debug={ADSGRAM_DEBUG}
        rewardAmount={ADSGRAM_REWARD_AMOUNT}
        rewardCurrency={ADSGRAM_REWARD_CURRENCY}
        onReward={onAdsgramReward}
      />
    ),
    [onAdsgramReward]
  );

  const monetagNode = (
  <div
    className="task-card"
    style={{
      height: "64px",
      marginTop: 8,
      opacity:
        monetagTaskState.claimedToday
          ? 0.72
          : 1,
    }}
  >
    <div className="icon">
      <img
        src="/calendar.png"
        alt=""
        style={{
          height: 32,
          width: 32,
          objectFit: "contain",
          borderRadius: 6,
        }}
      />
    </div>

    <div
      className="task"
      style={{
        minWidth: 0,
        textAlign: "left",
      }}
    >
      <h3
        style={{
          fontSize: 14,
          marginBottom: 4,
        }}
      >
        Visit Our Sponsor
      </h3>

      <div
        className="task-reward"
        style={{
          backgroundImage:
            "url('/sparks.png')",

          backgroundSize: "18px 18px",

          backgroundRepeat:
            "no-repeat",

          backgroundPosition:
            "left center",

          paddingLeft: 21,

          fontSize: monetagTaskMessage ? 11 : 16,
        }}
      >
        {monetagTaskMessage
          ? monetagTaskMessage
          : `${MONETAG_TASK_REWARD_AMOUNT} Sparks`
        }
      </div>
    </div>

    <div className="action">
      {monetagTaskState.claimedToday ? (
        <img
          src="./v2/check.png"
          alt=""
          style={{
            float: "right",
            marginTop: 15,
          }}
        />
      ) : (
        <button
          type="button"
          className="game-button-small yellow-button"
          onClick={handleMonetagTask}
          disabled={monetagTaskLoading}
          style={{
            opacity:
              monetagTaskLoading
                ? 0.6
                : 1,
          }}
        >
          {monetagTaskLoading
            ? "WAIT"
            : "OPEN"}
        </button>
      )}
    </div>
  </div>
);

  const activeModalTasks = activeModal ? grouped[activeModal.key] || [] : [];

  return (
    <>
      <div
        className="shop-container" style={{paddingBottom: 0}}>
        <div style={{ width: "100%", maxWidth: 450, margin: "0 auto" }}>
          {loading && (
  <div
    style={{
      minHeight: 430,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner text="Loading Tasks" size={64} />
  </div>
)}

          {!loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: -10 }}>
              
<TasksSection
  title="Special Tasks"
  showCount={false}
  showAction={false}
  titleClassName="modal_title_special"
>
  {boinkersTask && renderTaskCard(boinkersTask, 0)}

  {adsgramNode}

  {monetagNode}

  {BOUNTY_TASK_ENABLED && renderTaskCard(BOUNTY_TASK, 3)}

  {storyTasks.map((story) => renderStoryTask(story))}
</TasksSection>

<TasksSection
  title="Partnership Tasks"
  count={getUnfinishedCount(grouped.partnerships)}
  showAction={false}
  titleClassName="modal_title_partnership"
>
  <div
    onClick={() =>
      setActiveModal({
        key: "partnerships",
        label: "Partnership Tasks",
      })
    }
    role="button"
    tabIndex={0}
    style={{
      position: "relative",
      width: "100%",
      height: 142,
      margin: "0 auto",
      cursor: "pointer",
      background: "linear-gradient(180deg, #a83f20 0%, #8d341c 100%)",
      border: "3px solid #ffd84a",
      borderRadius: 8,
      boxShadow: "0 4px 0 #6b2415, inset 0 0 0 2px rgba(80, 20, 8, 0.45)",
      overflow: "hidden",
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(90deg, rgba(80,20,8,0.25) 0%, rgba(255,180,80,0.10) 50%, rgba(80,20,8,0.15) 100%)",
        pointerEvents: "none",
      }}
    />

    <img
      src="/v2/taskchar.png"
      alt=""
      style={{
        position: "absolute",
        left: 0,
        bottom: -2,
        height: 142,
        objectFit: "contain",
        zIndex: 2,
        pointerEvents: "none",
      }}
    />

    <div
      style={{
        position: "absolute",
        right: 10,
        top: 10,
        height: 39,
        display: "flex",
        gap: 4,
        zIndex: 3,
      }}
    >
      {[
        "/sparks.png",
        "/runes.png",
        "/pack.png",
        "/energy.png",
        "/shop_chest_1.png",
      ].map((icon, idx) => (
        <div
          key={idx}
          style={{
            width: 39,
            height: 39,
            background: "rgba(68, 25, 13, 0.88)",
            border: "2px solid #d99024",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
            boxShadow: "inset 0 0 0 1px rgba(255,255,120,0.18)",
          }}
        >
          <img
            src={icon}
            alt=""
            style={{
              width: 28,
              height: 28,
              objectFit: "contain",
            }}
          />
        </div>
      ))}
    </div>

    <div
      style={{
        position: "absolute",
        right: 10,
        top: 52,
        zIndex: 3,
        minWidth: 0,
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 15,
          fontWeight: 600,
          marginTop: 2,
          textAlign: "right"
        }}
      >
        Complete partner missions
        <br />
        and claim rewards
      </div>
    </div>

    <button
      type="button"
      className="game-button-small yellow-button"
      onClick={(e) => {
        e.stopPropagation();
        setActiveModal({
          key: "partnerships",
          label: "Partnership Tasks",
        });
      }}
      style={{
        position: "absolute",
        right: 10,
        bottom: 10,
        zIndex: 5,
      }}
    >
      OPEN PARTNERSHIP TASKS
    </button>
  </div>
</TasksSection>

              {sections.map((section) => {
  const list = grouped[section.key] || [];

  if (list.length === 0) return null;

  const preview = list.slice(0, PREVIEW_LIMIT);

  return (
    <TasksSection
      key={section.key}
      title={section.label}
      count={getUnfinishedCount(list)}
      actionLabel="SHOW ALL"
      onClick={() => setActiveModal(section)}
    >
      {preview.map((task, idx) => renderTaskCard(task, idx))}
    </TasksSection>
  );
})}

             
            </div>
          )}
        </div>
      </div>

            {activeModal && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            {activeModal.label}

            <button
              type="button"
              className="modal_close"
              onClick={() => setActiveModal(null)}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          style={{ zIndex: 99999 }}>
            <div className="modal_inside_2">
              {activeModalTasks.length > 0 ? (
                activeModalTasks.map((task, idx) => renderTaskCard(task, idx))
              ) : (
                <EmptyTasks text="No tasks available right now." />
              )}
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button red-button"
                onClick={() => setActiveModal(null)}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {errorMessage && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            Error

            <button
              type="button"
              className="modal_close"
              onClick={() => setErrorMessage("")}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          style={{ zIndex: 100000 }}>
            <div className="modal_inside">
              <img
                src="/finger.png"
                alt=""
                style={{
                  height: "80px",
                  marginBottom: "10px",
                  transform: "rotate(180deg)",
                }}
              />

              <div
                className="shadow1"
                style={{
                  fontSize: 22,
                  color: "#fff",
                  textTransform: "capitalize",
                }}
              >
                {errorMessage}
              </div>
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button red-button"
                onClick={() => setErrorMessage("")}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {successModalTask && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            Verification Pending

            <button
              type="button"
              className="modal_close"
              onClick={() => setSuccessModalTask(null)}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          style={{ zIndex: 100000 }}>
            <div className="modal_inside">
              <div className="shadow2" style={{ fontSize: 28, fontWeight: 600, color: "#fff" }}>
                Task Opened
              </div>

              <p
                className="shadow1"
                style={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: "#ddd",
                  lineHeight: "24px",
                  margin: "10px 0 0",
                }}
              >
                We&apos;ll manually verify this soon.
                <br />
                You can claim the reward after the timer finishes.
              </p>
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button green-button"
                onClick={() => setSuccessModalTask(null)}
              >
                OK
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default TasksPage;